import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

export interface ModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Modal content */
  children: React.ReactNode;
  /** Custom className for modal content */
  className?: string;
  /** Show close button in header */
  showCloseButton?: boolean;
  /** Close modal on Escape key press */
  closeOnEscape?: boolean;
  /** Modal size */
  size?: 'small' | 'medium' | 'large' | 'xlarge' | 'full';
}

/**
 * Modal dialog component with backdrop
 *
 * @example
 * ```tsx
 * <Modal open={isOpen} onClose={handleClose} title="Confirm Delete">
 *   <p>Are you sure you want to delete this item?</p>
 *   <Button onClick={handleDelete}>Delete</Button>
 * </Modal>
 * ```
 */
export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  className,
  showCloseButton = true,
  closeOnEscape = true,
  size = 'medium',
}) => {
  const { t } = useTranslation('common');

  // Handle Escape key press
  useEffect(() => {
    if (!open || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, closeOnEscape, onClose]);

  if (!open) return null;

  const handleBackdropClick = () => {
    onClose();
  };

  const handleContentClick = (e: React.MouseEvent) => {
    // Prevent backdrop click from triggering when clicking modal content
    e.stopPropagation();
  };

  const sizeClasses = {
    small: 'max-w-sm',
    medium: 'max-w-md',
    large: 'max-w-2xl',
    xlarge: 'max-w-5xl',
    full: 'max-w-7xl',
  };

  return (
    <div
      className="modal-backdrop"
      data-testid="modal-backdrop"
      onClick={handleBackdropClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'bg-white rounded-2xl shadow-2xl',
          'w-full mx-4',
          sizeClasses[size],
          'transform transition-all',
          'max-h-[90vh] flex flex-col',
          'pb-safe',
          className
        )}
        onClick={handleContentClick}
        style={{ animation: 'slideUp 300ms ease-out' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 md:p-6 border-b border-gray-200/60 flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          {showCloseButton && (
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
              aria-label={t('aria.close_modal')}
            >
              <svg
                className="w-5 h-5"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-5 md:p-6 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>
  );
};

Modal.displayName = 'Modal';
